import React, { useMemo } from 'react';

import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';

import { useMarketLineSelection } from '../../hooks/use-market-line-selection';
import { Event, Market, Team } from '../../types';
import { ViewAltLinesButton } from './ViewAltLinesButton';
import { OptionGridLayout } from './option-button-grid/OptionGridLayout';

type MultiSplitStandardMarketProps = {
    markets: Array<Market>;
    event: Event;
    testID?: string;
};

// Per-team sections from either: one market per team (params.team_id), or a single market
// whose options carry a TEAM param (0 = home, 1 = away, e.g. GAME_CORRECT_SCORE). Away first.
export const MultiSplitStandardMarket = ({ markets, event, testID }: MultiSplitStandardMarketProps) => {
    const sides: Array<{ team?: Team; teamValue: number }> = [
        { team: event?.away_team, teamValue: 1 },
        { team: event?.home_team, teamValue: 0 },
    ];

    return (
        <Box flex={1} gap="s12">
            {sides.map(({ team, teamValue }) =>
                team ? (
                    <TeamSection
                        key={team.id}
                        team={team}
                        teamValue={teamValue}
                        markets={markets}
                        event={event}
                        testID={testID}
                    />
                ) : null
            )}
        </Box>
    );
};

type TeamSectionProps = {
    team: Team;
    teamValue: number;
    markets: Array<Market>;
    event: Event;
    testID?: string;
};

const TeamSection = ({ team, teamValue, markets, event, testID }: TeamSectionProps) => {
    // markets keyed by this team's team_id (alt lines selectable per team)
    const teamMarkets = markets.filter(m => m.market_type.params.team_id === team.id);
    const { hasAlternateMarkets, selectedMarket, showAlternateMarkets } = useMarketLineSelection({
        eventId: event.id,
        markets: teamMarkets,
        marketAppendTeamName: true,
    });
    // fallback: split a single market's options by their TEAM param
    const optionSplitMarket = useMemo(() => {
        if (teamMarkets.length) {
            return undefined;
        }
        const base = markets[0];
        const options = base?.options.filter(option => option.option_type.params.TEAM === teamValue) ?? [];
        return base && options.length ? { ...base, options } : undefined;
    }, [markets, teamMarkets.length, teamValue]);

    const market = teamMarkets.length ? selectedMarket : optionSplitMarket;
    if (!market?.options.length) {
        return null;
    }

    return (
        <Column>
            <Row marginTop="s8" marginBottom="s12">
                <Text color="neutral300" variant="bodySmall">
                    {team.alias}
                </Text>
            </Row>
            <OptionGridLayout columns={2} event={event} market={market} testID={testID} />
            {hasAlternateMarkets ? (
                <Box mt={'s16'}>
                    <ViewAltLinesButton onPress={showAlternateMarkets} />
                </Box>
            ) : null}
        </Column>
    );
};
